/**
 * ROAS Dashboard
 * ROAS forecasting, budget optimization, and channel recommendations
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Download,
  AlertCircle,
  Target,
  PieChart,
  BarChart3,
} from 'lucide-react';
import { api } from '../../services/api';
import { useDataSourceStore } from '@store/dataSourceStore';
import DateRangeFilter, { DateRange } from '@components/analytics/DateRangeFilter';
import toast from 'react-hot-toast';

interface ChannelForecast {
  channel: string;
  currentSpend: number;
  currentRevenue: number;
  currentROAS: number;
  forecastSpend: number;
  forecastRevenue: number;
  forecastROAS: number;
  confidence: number;
}

interface BudgetOptimization {
  channel: string;
  currentSpend: number;
  recommendedSpend: number;
  change: number;
  changePercent: number;
  expectedRevenue: number;
  expectedROAS: number;
}

const ROASDashboard = () => {
  // Get selected company and data source from store
  const { selectedCompany, selectedDataSources, getSelection } = useDataSourceStore();

  // Filter to only use data sources that support 'roas' or 'attribution' module
  const roasDataSources = selectedDataSources.filter(ds =>
    (ds.availableModules || []).includes('attribution' as any) || 
    (ds.availableModules || []).includes('analytics')
  );
  const selectedDataSource = roasDataSources[0] || null;

  const { companyId } = getSelection();
  const dataSourceId = selectedDataSource?.id || null;
  const currency = selectedCompany?.settings?.currency || 'GBP';

  // Date range state (default: Last 90 days for better forecasting) - Load from localStorage
  const getDefaultDateRange = (): DateRange => {
    // Try to load from localStorage first
    const saved = localStorage.getItem('roas-dateRange');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved date range', e);
      }
    }
    
    // Default fallback
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 90);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label: 'Last 90 days',
    };
  };

  // Load budget and optimization from localStorage
  const getStoredBudget = () => {
    const saved = localStorage.getItem('roas-totalBudget');
    return saved || '10000';
  };

  const getStoredOptimization = () => {
    const saved = localStorage.getItem('roas-showBudgetOptimization');
    return saved === 'true';
  };

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [totalBudget, setTotalBudget] = useState<string>(getStoredBudget());
  const [showBudgetOptimization, setShowBudgetOptimization] = useState(getStoredOptimization());

  // Save to localStorage whenever values change
  useEffect(() => {
    localStorage.setItem('roas-dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem('roas-totalBudget', totalBudget);
  }, [totalBudget]);

  useEffect(() => {
    localStorage.setItem('roas-showBudgetOptimization', String(showBudgetOptimization));
  }, [showBudgetOptimization]);

  // Fetch ROAS forecast
  const {
    data: forecastResponse,
    isLoading: forecastLoading,
    refetch: refetchForecast,
  } = useQuery({
    queryKey: [
      'roas-forecast',
      companyId,
      dataSourceId,
      dateRange.startDate,
      dateRange.endDate,
      showBudgetOptimization ? totalBudget : null,
    ],
    queryFn: async () => {
      if (!companyId) throw new Error('Please select a company');

      const params: any = {
        companyId,
        dataSourceId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      if (showBudgetOptimization && totalBudget) {
        params.totalBudget = totalBudget;
      }

      const response = await api.get('/api/v1/analytics/attribution/roas-forecast', { params });
      return response.data;
    },
    enabled: !!companyId,
  });

  const forecasts: ChannelForecast[] = forecastResponse?.data?.forecasts || [];
  const currentPerformance = forecastResponse?.data?.currentPerformance || [];
  const budgetOptimization: BudgetOptimization[] = forecastResponse?.data?.budgetOptimization || [];
  const modelAccuracy = forecastResponse?.data?.modelAccuracy || {};

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  const getChannelColor = (channel: string) => {
    const colors: Record<string, string> = {
      email: 'bg-blue-100 text-blue-800 border-blue-300',
      meta: 'bg-purple-100 text-purple-800 border-purple-300',
      google: 'bg-red-100 text-red-800 border-red-300',
      awin: 'bg-green-100 text-green-800 border-green-300',
      tiktok: 'bg-pink-100 text-pink-800 border-pink-300',
      direct: 'bg-gray-100 text-gray-800 border-gray-300',
    };
    return colors[channel.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getAccuracyColor = (mape: number) => {
    if (mape < 10) return 'text-green-600 dark:text-green-400';
    if (mape < 20) return 'text-yellow-600 dark:text-yellow-400';
    if (mape < 30) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const handleRefresh = async () => {
    await refetchForecast();
    toast.success('Forecast refreshed!');
  };

  const handleExport = () => {
    const data = showBudgetOptimization
      ? budgetOptimization.map(b => [
          b.channel,
          b.currentSpend,
          b.recommendedSpend,
          b.change,
          b.changePercent,
          b.expectedRevenue,
          b.expectedROAS,
        ])
      : forecasts.map(f => [
          f.channel,
          f.currentSpend,
          f.currentROAS,
          f.forecastSpend,
          f.forecastROAS,
          f.confidence,
        ]);

    const headers = showBudgetOptimization
      ? ['Channel', 'Current Spend', 'Recommended', 'Change', 'Change %', 'Expected Revenue', 'Expected ROAS']
      : ['Channel', 'Current Spend', 'Current ROAS', 'Forecast Spend', 'Forecast ROAS', 'Confidence'];

    const csv = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `roas-forecast-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported successfully!');
  };

  const handleRecalculate = async () => {
    if (!companyId) {
      toast.error('Please select a company');
      return;
    }

    try {
      toast.loading('Recalculating attribution...', { id: 'recalc' });
      await api.post('/api/v1/analytics/attribution/recalculate', {
        companyId,
        lookbackDays: 90,
      });
      toast.success('Attribution recalculated!', { id: 'recalc' });
      await refetchForecast();
    } catch (error: any) {
      toast.error(error.message || 'Failed to recalculate', { id: 'recalc' });
    }
  };

  const isLoading = forecastLoading;

  if (!companyId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <DollarSign size={64} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            ROAS Forecast
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please select a company to view ROAS forecasts
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <DollarSign className="text-green-500" />
              ROAS Forecast & Budget Optimization
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Predict ROAS and optimize marketing budget allocation
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRecalculate}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
            >
              <Target size={18} />
              Recalculate
            </button>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
            >
              <Download size={18} />
              Export
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </div>

        {/* Data Source Info */}
        {selectedDataSource && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Data Source:</strong> {selectedDataSource.name} ({selectedDataSource.type})
              {selectedDataSource.purpose && ` - ${selectedDataSource.purpose}`}
            </p>
          </div>
        )}

        {/* Model Accuracy Card */}
        {modelAccuracy.mape !== undefined && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Model Accuracy
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Mean Absolute Percentage Error (MAPE)
                </p>
              </div>
              <div className="text-right">
                <div className={`text-3xl font-bold ${getAccuracyColor(modelAccuracy.mape)}`}>
                  {modelAccuracy.mape.toFixed(2)}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {modelAccuracy.description}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Budget Optimization Toggle */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showBudgetOptimization}
                  onChange={(e) => setShowBudgetOptimization(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="font-medium text-gray-900 dark:text-white">
                  Budget Optimization
                </span>
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">
                Get AI-powered recommendations for budget allocation
              </p>
            </div>
            {showBudgetOptimization && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total Budget:
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    {currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '€'}
                  </span>
                  <input
                    type="number"
                    value={totalBudget}
                    onChange={(e) => setTotalBudget(e.target.value)}
                    className="pl-8 pr-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg w-32"
                    min="0"
                    step="1000"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-12 shadow-md flex justify-center items-center">
            <RefreshCw className="animate-spin text-indigo-500" size={48} />
          </div>
        ) : (
          <>
            {/* Budget Optimization View */}
            {showBudgetOptimization && budgetOptimization.length > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <PieChart size={24} className="text-indigo-500" />
                    Recommended Budget Allocation
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Based on Bayesian media-mix modeling and historical performance
                  </p>
                </div>
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                            Channel
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                            Current Spend
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                            Recommended
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                            Change
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                            Expected Revenue
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                            Expected ROAS
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {budgetOptimization.map((item) => (
                          <tr key={item.channel} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-4 py-4">
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-medium border ${getChannelColor(
                                  item.channel
                                )}`}
                              >
                                {item.channel}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                              {formatCurrency(item.currentSpend)}
                            </td>
                            <td className="px-4 py-4 text-sm font-bold text-indigo-600 dark:text-indigo-400 text-right">
                              {formatCurrency(item.recommendedSpend)}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {item.changePercent > 0 ? (
                                  <TrendingUp size={16} className="text-green-600 dark:text-green-400" />
                                ) : item.changePercent < 0 ? (
                                  <TrendingDown size={16} className="text-red-600 dark:text-red-400" />
                                ) : null}
                                <span
                                  className={`text-sm font-medium ${
                                    item.changePercent > 0
                                      ? 'text-green-600 dark:text-green-400'
                                      : item.changePercent < 0
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-gray-600 dark:text-gray-400'
                                  }`}
                                >
                                  {item.changePercent > 0 ? '+' : ''}
                                  {item.changePercent.toFixed(1)}%
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm font-medium text-green-600 dark:text-green-400 text-right">
                              {formatCurrency(item.expectedRevenue)}
                            </td>
                            <td className="px-4 py-4 text-sm font-bold text-gray-900 dark:text-white text-right">
                              {item.expectedROAS.toFixed(2)}x
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              /* Forecast View */
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <BarChart3 size={24} className="text-indigo-500" />
                    ROAS Forecast by Channel
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Predicted performance for next period based on Markov Chain attribution
                  </p>
                </div>
                <div className="p-6">
                  {forecasts.length === 0 ? (
                    <div className="text-center py-12">
                      <AlertCircle size={48} className="text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">
                        No forecast data available. Upload data or adjust date range.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {forecasts.map((forecast) => (
                        <div
                          key={forecast.channel}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium border ${getChannelColor(
                                forecast.channel
                              )}`}
                            >
                              {forecast.channel}
                            </span>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Confidence: {(forecast.confidence * 100).toFixed(0)}%
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                            <div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Current Spend
                              </div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatCurrency(forecast.currentSpend)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Current Revenue
                              </div>
                              <div className="text-sm font-medium text-green-600 dark:text-green-400">
                                {formatCurrency(forecast.currentRevenue)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Current ROAS
                              </div>
                              <div className="text-sm font-bold text-gray-900 dark:text-white">
                                {forecast.currentROAS.toFixed(2)}x
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Forecast Spend
                              </div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatCurrency(forecast.forecastSpend)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Forecast Revenue
                              </div>
                              <div className="text-sm font-medium text-green-600 dark:text-green-400">
                                {formatCurrency(forecast.forecastRevenue)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
                                Forecast ROAS
                                {forecast.forecastROAS > forecast.currentROAS ? (
                                  <TrendingUp size={12} className="text-green-600" />
                                ) : forecast.forecastROAS < forecast.currentROAS ? (
                                  <TrendingDown size={12} className="text-red-600" />
                                ) : null}
                              </div>
                              <div
                                className={`text-sm font-bold ${
                                  forecast.forecastROAS > forecast.currentROAS
                                    ? 'text-green-600 dark:text-green-400'
                                    : forecast.forecastROAS < forecast.currentROAS
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-gray-900 dark:text-white'
                                }`}
                              >
                                {forecast.forecastROAS.toFixed(2)}x
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Current Performance Summary */}
            {currentPerformance.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Current Performance Snapshot
                  </h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {currentPerformance.map((channel: any) => (
                      <div
                        key={channel.channel}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getChannelColor(
                              channel.channel
                            )}`}
                          >
                            {channel.channel}
                          </span>
                          <span
                            className={`text-lg font-bold ${
                              channel.roas > 3
                                ? 'text-green-600'
                                : channel.roas > 1
                                ? 'text-yellow-600'
                                : 'text-red-600'
                            }`}
                          >
                            {channel.roas.toFixed(2)}x
                          </span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Spend:</span>
                            <span className="font-medium">{formatCurrency(channel.spend)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Revenue:</span>
                            <span className="font-medium text-green-600">{formatCurrency(channel.revenue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Orders:</span>
                            <span className="font-medium">{Math.round(channel.orders)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">CPA:</span>
                            <span className="font-medium">{formatCurrency(channel.cpa)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ROASDashboard;
